export type ExtraSeatOccupant = {
    _id: string;
    name?: string;
    lastName?: string;
    createdAt?: string | Date | null;
    status?: string;
    isOwner?: boolean;
    permissions?: {
        COMMERCE?: string[];
        POS?: unknown[];
        INVENTORY?: unknown[];
    } | null;
};

export type ExtraSeatRow = {
    key: string;
    kind: 'included' | 'extra';
    occupant: ExtraSeatOccupant | null;
    isCurrentUser: boolean;
    roleLabel: string;
    displayName: string;
};

export const EMPTY_SEAT_DISPLAY_NAME = 'Cupo disponible';

export function extraSeatDisplayName(occupant: ExtraSeatOccupant | null): string {
    if (!occupant) {
        return '';
    }
    return [occupant.name, occupant.lastName].filter(Boolean).join(' ').trim() || 'Usuario';
}

export function extraSeatRoleLabel(occupant: ExtraSeatOccupant | null, ownerId?: string | null): string {
    if (!occupant) {
        return '';
    }
    if (occupant.isOwner || (ownerId && String(occupant._id) === String(ownerId))) {
        return 'Administrador';
    }
    const commerce = occupant.permissions?.COMMERCE;
    if (Array.isArray(commerce) && commerce.includes('create-and-edit-users')) {
        return 'Administrador';
    }
    if (Array.isArray(occupant.permissions?.POS) && occupant.permissions.POS.length > 0) {
        return 'Caja';
    }
    if (Array.isArray(occupant.permissions?.INVENTORY) && occupant.permissions.INVENTORY.length > 0) {
        return 'Almacén';
    }
    return 'Usuario';
}

/** Más antiguos primero: ellos ocupan los cupos incluidos (plan + extendUsers). Sin fecha = más reciente. */
export function sortSeatOccupants(occupants: ExtraSeatOccupant[]): ExtraSeatOccupant[] {
    return [...occupants].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
    });
}

/**
 * Una fila por cupo, ocupado o no (ADM-646 hotfix): «Eres tú», incluidos ocupados,
 * incluidos vacíos, adicionales ocupados y adicionales vacíos. El total de filas es
 * `max(includedSeats + pendingPurchasedUsers, ocupantes)`: los ocupantes que
 * desbordan el total (heredados) se listan igual, sin inventar cupos vacíos.
 */
export function buildExtraUserSeatRows({
    occupants,
    includedSeats,
    pendingPurchasedUsers,
    currentUserId,
    ownerId,
}: {
    occupants: ExtraSeatOccupant[];
    includedSeats: number;
    pendingPurchasedUsers: number;
    currentUserId?: string | null;
    ownerId?: string | null;
}): ExtraSeatRow[] {
    const included = Math.max(0, Math.floor(Number(includedSeats) || 0));
    const extras = Math.max(0, Math.floor(Number(pendingPurchasedUsers) || 0));
    const current = currentUserId ? String(currentUserId) : '';

    const eligible = (occupants || []).filter(occupant => occupant?._id && occupant.status !== 'bloqueado' && occupant.status !== 'eliminado');
    const currentOccupant = current ? eligible.find(occupant => String(occupant._id) === current) : undefined;
    const others = sortSeatOccupants(eligible.filter(occupant => String(occupant._id) !== current));

    // "Eres tú" va primero y ocupa 1 cupo incluido (plan + extendUsers), aunque el plan no traiga ninguno.
    const remainingIncluded = currentOccupant ? Math.max(0, included - 1) : included;
    const includedOthers = others.slice(0, remainingIncluded);
    const extraOccupants = others.slice(remainingIncluded);
    const includedOccupied = (currentOccupant ? 1 : 0) + includedOthers.length;
    const emptyIncluded = Math.max(0, included - includedOccupied);
    // Si "Eres tú" ocupa un incluido que no existe, consume uno de los adicionales.
    const includedOverflow = Math.max(0, includedOccupied - included);
    const emptyExtra = Math.max(0, extras - extraOccupants.length - includedOverflow);

    const rows: ExtraSeatRow[] = [];

    if (currentOccupant) {
        rows.push({
            key: `current-${currentOccupant._id}`,
            kind: 'included',
            occupant: currentOccupant,
            isCurrentUser: true,
            roleLabel: extraSeatRoleLabel(currentOccupant, ownerId),
            displayName: extraSeatDisplayName(currentOccupant),
        });
    }

    for (const occupant of includedOthers) {
        rows.push({
            key: `included-${occupant._id}`,
            kind: 'included',
            occupant,
            isCurrentUser: false,
            roleLabel: extraSeatRoleLabel(occupant, ownerId),
            displayName: extraSeatDisplayName(occupant),
        });
    }

    for (let index = 0; index < emptyIncluded; index += 1) {
        rows.push(emptySeatRow('included', index));
    }

    for (const occupant of extraOccupants) {
        rows.push({
            key: `extra-${occupant._id}`,
            kind: 'extra',
            occupant,
            isCurrentUser: false,
            roleLabel: extraSeatRoleLabel(occupant, ownerId),
            displayName: extraSeatDisplayName(occupant),
        });
    }

    for (let index = 0; index < emptyExtra; index += 1) {
        rows.push(emptySeatRow('extra', index));
    }

    return rows;
}

function emptySeatRow(kind: ExtraSeatRow['kind'], index: number): ExtraSeatRow {
    return {
        key: `${kind}-empty-${index}`,
        kind,
        occupant: null,
        isCurrentUser: false,
        roleLabel: '',
        displayName: EMPTY_SEAT_DISPLAY_NAME,
    };
}
